import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { apiPut } from "@/shared/api/client";
import { billingProfileForm, type BillingProfileForm as ProfileValues } from "@/shared/api/contracts";
import { describeError, isApiError, type ApiError } from "@/shared/api/errors";
import { invalidateTenantScopes } from "@/shared/api/invalidate";
import type { BillingProfileBody, BillingProfileDto } from "@/shared/api/types";
import { useTenantId } from "@/shared/auth/session";

/**
 * The billing details form, once (ADR-0012). Two surfaces fill it in: the Billing details card on
 * Settings → Subscription, edited at any time, and the step a purchase dialog turns into when the api
 * answers `BILLING_PROFILE_REQUIRED` — which is started from the locked add-on panels and the limit
 * notices too, so it lives in `shared/` (rules/01 §1). One seed, one body, one error mapping: two copies
 * would drift on exactly the optional-field rule below.
 */

const EMPTY: ProfileValues = {
  legalName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postcode: "",
  country: "IN",
  gstin: "",
  billingEmail: "",
};

function valuesFrom(profile: BillingProfileDto | null): ProfileValues {
  if (!profile) return EMPTY;
  return {
    legalName: profile.legalName,
    addressLine1: profile.addressLine1,
    addressLine2: profile.addressLine2 ?? "",
    city: profile.city,
    state: profile.state,
    postcode: profile.postcode,
    country: profile.country,
    gstin: profile.gstin ?? "",
    billingEmail: profile.billingEmail ?? "",
  };
}

/** Blank optional fields are dropped, so clearing one sends "not set" rather than an empty string. */
function bodyFrom(values: ProfileValues): BillingProfileBody {
  return {
    legalName: values.legalName,
    addressLine1: values.addressLine1,
    ...(values.addressLine2 ? { addressLine2: values.addressLine2 } : {}),
    city: values.city,
    state: values.state,
    postcode: values.postcode,
    country: values.country,
    ...(values.gstin ? { gstin: values.gstin } : {}),
    ...(values.billingEmail ? { billingEmail: values.billingEmail } : {}),
  };
}

const isField = (key: string | null | undefined): key is keyof ProfileValues => !!key && key in EMPTY;

export function useSaveBillingProfile() {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (body: BillingProfileBody) => apiPut<BillingProfileDto>("/billing/profile", body),
    onSuccess: () => invalidateTenantScopes(queryClient, tenantId, ["billing"]),
  });
}

/**
 * The form over `GET /billing`'s `profile`. `submit(onSaved)` saves, re-seeds from what the api stored
 * and hands that on; a `VALIDATION_ERROR` lands on the fields it names and anything else is a toast, and
 * in both cases `onSaved` is not called — a step that continues to payment must not continue on a
 * profile the api did not take.
 *
 * `refusal` is the `BILLING_PROFILE_REQUIRED` that sent a dialog here. When the api names the one field
 * the gateway wanted (a billing email, `gatewayCustomer.ts`), the message goes on that field, so the
 * customer is not left guessing which of nine boxes is the problem.
 */
export function useBillingProfileForm(profile: BillingProfileDto | null, refusal: ApiError | null = null) {
  const save = useSaveBillingProfile();
  const form = useForm<ProfileValues>({
    resolver: zodResolver(billingProfileForm),
    defaultValues: valuesFrom(profile),
  });

  // After the re-seed, because a reset clears every error — including the field the gateway named.
  useEffect(() => {
    if (!form.formState.isDirty) form.reset(valuesFrom(profile));
    const field = refusal?.detailString("field");
    if (refusal && isField(field)) form.setError(field, { message: refusal.message });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed from fresh server data only while untouched
  }, [profile?.updatedAt, refusal]);

  const submit = (onSaved: (stored: BillingProfileDto) => void | Promise<void>) =>
    form.handleSubmit(async (values) => {
      let stored: BillingProfileDto;
      try {
        stored = await save.mutateAsync(bodyFrom(values));
      } catch (error) {
        if (isApiError(error) && error.code === "VALIDATION_ERROR") {
          for (const issue of error.validationIssues) {
            const key = issue.path.replace(/^\/?(body\/)?/, "").split(/[/.]/)[0];
            if (isField(key)) form.setError(key, { message: issue.message });
          }
          return;
        }
        const { title, detail } = describeError(error);
        toast.error(title, detail ? { description: detail } : undefined);
        return;
      }
      form.reset(valuesFrom(stored));
      await onSaved(stored);
    });

  return { form, save, submit, discard: () => form.reset(valuesFrom(profile)) };
}
